import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {MvsCrudService} from 'features/core/shared/service/crud/mvs-crud.service';
import {CnContractCalculationPageDto} from '../../../model/dto/api/cn-contract-calculation-page.dto';
import {CnCalculationTotalsDto} from '../../../model/dto/api/cn-calculation-totals.dto';
import {CnCalculationByPartnerDto} from '../../../model/dto/api/cn-calculation-by-partner.dto';
import {CnCalculationTrendDto} from '../../../model/dto/api/cn-calculation-trend.dto';
import {CnRecalcResultDto} from '../../../model/dto/api/cn-recalc-result.dto';

/**
 * API service for the CALCULATED commission records (Soll) REST endpoints.
 *
 * Endpoint mapping:
 *   GET  /cn/contract/{contractId}/calculations            → getCalculations()
 *   GET  /cn/contract/{contractId}/calculations/summary    → getContractSummary()
 *   GET  /cn/calculation/totals                            → getTotals()
 *   GET  /cn/calculation/totals/by-partner                 → getTotalsByPartner()
 *   GET  /cn/calculation/trend                             → getCalculationTrend()
 *   POST /cn/commission-recalc/{partnerId}/recalculate     → recalculate()
 */
@Injectable({
    providedIn: 'root'
})
export class CnCalculationApiService {

    private readonly contractBaseUrl = MvsCrudService.baseUrl + '/cn/contract';
    private readonly calculationBaseUrl = MvsCrudService.baseUrl + '/cn/calculation';
    private readonly recalcBaseUrl = MvsCrudService.baseUrl + '/cn/commission-recalc';

    constructor(private http: HttpClient) {
    }

    /**
     * GET /cn/contract/{contractId}/calculations
     *
     * Returns one page of CALCULATED commission records (Soll) for the given contract,
     * ordered oldest booking month first. Designed for the Vertragsabrechnung lazy table.
     *
     * @param contractId the contract whose calculation records to load (read-access enforced server-side)
     * @param page       0-based page index (defaults to 0)
     * @param size       page size (defaults to 50, clamped server-side)
     * @returns a typed Observable of the paged envelope (content + totalElements + page + size)
     */
    getCalculations(
        contractId: number,
        page: number = 0,
        size: number = 50
    ): Observable<CnContractCalculationPageDto> {
        const params = new HttpParams()
            .set('page', String(page))
            .set('size', String(size));
        return this.http.get<CnContractCalculationPageDto>(
            `${this.contractBaseUrl}/${contractId}/calculations`,
            {params}
        );
    }

    /**
     * GET /cn/contract/{contractId}/calculations/summary
     *
     * Returns the AP / BP / Sonstige / Gesamt EUR totals from CALCULATED records
     * for the given contract. Used for the Vertragsabrechnung summary header.
     *
     * @param contractId the contract whose calculation summary to load
     * @returns a typed Observable of the totals envelope
     */
    getContractSummary(contractId: number): Observable<CnCalculationTotalsDto> {
        return this.http.get<CnCalculationTotalsDto>(
            `${this.contractBaseUrl}/${contractId}/calculations/summary`
        );
    }

    /**
     * GET /cn/calculation/totals
     *
     * Returns the AP / BP / Sonstige / Gesamt EUR totals from CALCULATED records (Soll) in the
     * requested scope. Used for the "Berechnete Provision (Soll)" dashboard card. Each bucket
     * carries a single total (no Offen/Quittiert split — that is an Ist-side concept).
     *
     * @param period     optional single YearMonth filter ("YYYY-MM"); omit for all periods (all months)
     * @param partnerId  optional partner (Gesellschaft) id filter; omit for all partners
     * @param fromPeriod optional inclusive range start YearMonth ("YYYY-MM"); omit for an open start
     * @param toPeriod   optional inclusive range end YearMonth ("YYYY-MM"); omit for an open end
     * @returns a typed Observable of the AP / BP / Sonstige / Gesamt totals envelope
     */
    getTotals(period?: string, partnerId?: number, fromPeriod?: string, toPeriod?: string): Observable<CnCalculationTotalsDto> {
        let params = new HttpParams();
        if (period !== undefined && period !== null && period !== '') {
            params = params.set('period', period);
        }
        if (partnerId !== undefined && partnerId !== null) {
            params = params.set('partnerId', String(partnerId));
        }
        if (fromPeriod !== undefined && fromPeriod !== null && fromPeriod !== '') {
            params = params.set('fromPeriod', fromPeriod);
        }
        if (toPeriod !== undefined && toPeriod !== null && toPeriod !== '') {
            params = params.set('toPeriod', toPeriod);
        }
        return this.http.get<CnCalculationTotalsDto>(
            `${this.calculationBaseUrl}/totals`,
            {params}
        );
    }

    /**
     * GET /cn/calculation/totals/by-partner
     *
     * Returns an array of AP / BP / Gesamt EUR sums from CALCULATED records per
     * Gesellschaft (partner) in the requested scope. Used for the
     * "Provisionen je Gesellschaft (Soll)" dashboard card.
     *
     * @param period     optional single YearMonth filter ("YYYY-MM"); omit for all periods
     * @param partnerId  optional partner (Gesellschaft) id filter; omit for all partners
     * @param fromPeriod optional inclusive range start YearMonth ("YYYY-MM"); omit for an open start
     * @param toPeriod   optional inclusive range end YearMonth ("YYYY-MM"); omit for an open end
     * @returns a typed Observable of the per-partner totals array (empty when no partner has records)
     */
    getTotalsByPartner(period?: string, partnerId?: number, fromPeriod?: string, toPeriod?: string): Observable<CnCalculationByPartnerDto[]> {
        let params = new HttpParams();
        if (period !== undefined && period !== null && period !== '') {
            params = params.set('period', period);
        }
        if (partnerId !== undefined && partnerId !== null) {
            params = params.set('partnerId', String(partnerId));
        }
        if (fromPeriod !== undefined && fromPeriod !== null && fromPeriod !== '') {
            params = params.set('fromPeriod', fromPeriod);
        }
        if (toPeriod !== undefined && toPeriod !== null && toPeriod !== '') {
            params = params.set('toPeriod', toPeriod);
        }
        return this.http.get<CnCalculationByPartnerDto[]>(
            `${this.calculationBaseUrl}/totals/by-partner`,
            {params}
        );
    }

    /**
     * GET /cn/calculation/trend
     *
     * Returns the BERECHNETE Provision (Soll) as a zero-filled, range-aware monthly time series
     * (one point per month inside the inclusive fromPeriod..toPeriod range, oldest first), each
     * carrying the AP / BP / Sonstige / Gesamt EUR totals. Backs the "Soll vs Ist (Verlauf)" line
     * chart, where the Soll Gesamt is plotted against the empfangene/quittierte Ist Gesamt.
     *
     * NO calculation in the FE — every EUR amount comes verbatim from the backend.
     *
     * @param fromPeriod optional inclusive range start YearMonth ("YYYY-MM"); omit for an open start
     * @param toPeriod   optional inclusive range end YearMonth ("YYYY-MM"); omit for an open end
     * @param partnerId  optional partner (Gesellschaft) id filter; omit for all partners
     * @returns a typed Observable of the monthly Soll trend envelope ({ points: [...] })
     */
    getCalculationTrend(fromPeriod?: string, toPeriod?: string, partnerId?: number): Observable<CnCalculationTrendDto> {
        let params = new HttpParams();
        if (fromPeriod !== undefined && fromPeriod !== null && fromPeriod !== '') {
            params = params.set('fromPeriod', fromPeriod);
        }
        if (toPeriod !== undefined && toPeriod !== null && toPeriod !== '') {
            params = params.set('toPeriod', toPeriod);
        }
        if (partnerId !== undefined && partnerId !== null) {
            params = params.set('partnerId', String(partnerId));
        }
        return this.http.get<CnCalculationTrendDto>(
            `${this.calculationBaseUrl}/trend`,
            {params}
        );
    }

    /**
     * POST /cn/commission-recalc/{partnerId}/recalculate
     *
     * Triggers an idempotent recalculation of the CALCULATED commission (Soll) for ONE Gesellschaft
     * (partner) across all its contracts (inserts / updates / removes the expected records). For
     * small partners the run executes synchronously and the returned DTO carries the final counters;
     * for large partners it is dispatched asynchronously and returns status "RUNNING" + the runId.
     *
     * @param partnerId the partner (Gesellschaft) whose commission to recalculate
     * @returns a typed Observable of the recalculation result (run ref, status, counters)
     */
    recalculate(partnerId: number): Observable<CnRecalcResultDto> {
        return this.http.post<CnRecalcResultDto>(
            `${this.recalcBaseUrl}/${partnerId}/recalculate`,
            {}
        );
    }
}
